import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { useField, Field } from 'react-final-form';
import { Trans } from 'react-i18next';
import { FlexColumn, Tooltip, FlexMiddle, FlexSpace } from '@ubeya/shared-web/components';
import { ReactComponent as BaseInfoIcon } from '@ubeya/shared-web/assets/info.svg';
import { ReactComponent as BaseLockIcon } from '@ubeya/shared-web/assets/lock.svg';
import { ReactComponent as NewTagIcon } from '@ubeya/shared-web/assets/new-tag.svg';
import { ReactComponent as LongNarrowArrowIcon } from '@ubeya/shared-web/assets/long-narrow-arrow.svg';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import { MINT_ACCOUNT_ID, EXETER_ACCOUNT_ID, SPE_ACCOUNT_ID } from '@ubeya/shared/utils/accounts';
import ChannelCheckboxField from './ChannelCheckboxField';

const PUSH_CHANNEL = 'notification.pushNotification';
const EMAIL_CHANNEL = 'notification.email';

const SEND_ALL_ACCOUNT_IDS = [
  MINT_ACCOUNT_ID,
  EXETER_ACCOUNT_ID,
  SPE_ACCOUNT_ID,
  ...(process.env.ENV !== 'PROD' ? [8490] : []) // DEV PREVIEW ONLY
];

const Wrapper = styled(FlexColumn)`
  flex: 1;
  overflow: hidden;
  max-height: 100%;
`;

const ScrollContent = styled(FlexColumn)`
  flex: 1;
  overflow: auto;
  padding: 24px;
  gap: 20px;
`;

const Section = styled(FlexColumn)`
  gap: 16px;
`;

const SectionTitle = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.black};
`;

const ChannelList = styled.div`
  display: flex;
  flex-direction: column;
`;

const SubLabel = styled.span`
  font-size: 14px;
  font-weight: 400;
`;

const SubLabelRow = styled(FlexSpace)`
  align-items: center;
  gap: 12px;
  line-height: 18px;
`;

const ResetToDefault = styled.button.attrs(() => ({ type: 'button' }))`
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.primary};

  &:hover {
    text-decoration: underline;
  }
`;

const ChannelLink = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 0;
`;

const ChannelLinkArrow = styled(LongNarrowArrowIcon)`
  flex: none;
  fill: ${({ theme }) => theme.colors.grey4};
`;

const ChannelLinkLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey4};
`;

const RadioRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
`;

const CustomRadio = styled.div`
  position: relative;
  width: 18px;
  height: 18px;
  flex: none;
  border-radius: 50%;
  border: 1.5px solid ${({ $checked, theme }) => ($checked ? theme.colors.greenPositive : theme.colors.grey2)};
  background: ${({ theme }) => theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 150ms;
`;

const RadioDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.greenPositive};
  display: ${({ $visible }) => ($visible ? 'block' : 'none')};
`;

const RadioLabel = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.black};
`;

const GradualLabelRow = styled(FlexMiddle)`
  gap: 8px;
`;

const InfoIcon = styled(BaseInfoIcon)`
  width: 16px;
  height: 16px;
  color: ${({ theme }) => theme.colors.grey4};
  flex: none;
`;

const BannerInfoIcon = styled(BaseInfoIcon)`
  width: 18px;
  height: 18px;
  color: ${({ theme }) => theme.colors.grey4};
  flex: none;
  margin-top: 1px;
`;

const LockIcon = styled(BaseLockIcon)`
  fill: ${({ theme }) => theme.colors.primary};
`;

const TooltipContent = styled.div`
  padding: 16px;
  border-radius: 4px;
  max-width: 400px;
`;

const TooltipText = styled.div`
  font-size: 14px;
  line-height: 1.5;
`;

const MessageInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  height: 40px;
  padding: 0 12px;
  line-height: 18px;
  border: 1px solid ${({ theme }) => theme.colors.grey2};
  border-radius: 4px;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.black};
  outline: none;
  transition: border-color 150ms;

  &:hover {
    border-color: ${({ theme }) => theme.colors.grey3};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey3};
  }
`;

const EmailSubjectInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  height: 40px;
  padding: 0 12px;
  line-height: 18px;
  border: 1px solid ${({ theme }) => theme.colors.grey2};
  border-radius: 4px;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.black};
  outline: none;
  transition: border-color 150ms, box-shadow 150ms;

  &:hover {
    border-color: ${({ theme }) => theme.colors.grey3};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}40;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey3};
  }
`;

const InfoBanner = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background: ${({ theme }) => theme.colors.grey1};
  border: 1px solid ${({ theme }) => theme.colors.grey2};
  border-radius: 8px;
  padding: 14px 16px;
  color: ${({ theme }) => theme.colors.black};
`;

const InfoBannerContent = styled(FlexColumn)`
  gap: 4px;
`;

const InfoBannerTitle = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.black};
`;

const InfoBannerBody = styled.div`
  font-size: 13px;
  line-height: 18px;
  color: ${({ theme }) => theme.colors.black};
`;

const InfoBannerBold = styled.span`
  font-weight: 500;
`;

const RadioSection = styled(FlexColumn)`
  gap: 16px;
`;

const SectionDivider = styled.div`
  flex: none;
  height: 1px;
  margin: 0 -24px;
  background: ${({ theme }) => theme.colors.grey1};
`;

const RadioOptionField = ({ name, value, label, disabled }) => (
  <Field
    name={name}
    render={({ input }) => {
      const checked = input.value === value;
      return (
        <RadioRow $disabled={disabled} onClick={() => !disabled && input.onChange(value)}>
          <CustomRadio $checked={checked}>
            <RadioDot $visible={checked} />
          </CustomRadio>
          {label}
        </RadioRow>
      );
    }}
  />
);

const PublishSettings = ({ toAddMessage = true, isCheckAvailabilityNeeded = false }) => {
  const { t, isAccountNetworkAccessSupplier } = useContext(ConfigContext);
  const [openChannel, setOpenChannel] = useState(PUSH_CHANNEL);
  const { accountId } = useAccount();
  const {
    input: { value: pushChecked }
  } = useField('notification.pushNotification');
  const {
    input: { value: emailChecked }
  } = useField('notification.email');
  const {
    input: { value: suffixValue, onChange: onSuffixChange }
  } = useField('notification.suffix');
  const {
    input: { value: emailSubjectValue }
  } = useField('notification.emailSubject');

  const isSendAll = SEND_ALL_ACCOUNT_IDS.includes(accountId);
  const emailDescription = t(isSendAll ? 'publishChannelEmailDescAll' : 'publishChannelEmailDesc');
  const channelLinkLabel = t(isSendAll ? 'publishConnectorAlso' : 'publishConnectorFallback');

  const getBannerData = () => {
    if (pushChecked && emailChecked) {
      return {
        label: t('publishBannerPushEmailLabel'),
        text: t(isSendAll ? 'publishBannerPushEmailTextAll' : 'publishBannerPushEmailText')
      };
    }
    if (pushChecked) {
      return {
        label: t('publishBannerPushLabel'),
        text: t('publishBannerPushText')
      };
    }
    if (emailChecked) {
      return {
        label: t('publishBannerEmailLabel'),
        text: t('publishBannerEmailText')
      };
    }
    return {
      label: t('publishBannerNoneLabel'),
      text: t('publishBannerNoneText')
    };
  };

  const toggleOpenChannel = (channelName) => setOpenChannel((prev) => (prev === channelName ? null : channelName));

  const handleCheckedChange = (channelName, isChecked) =>
    setOpenChannel((prev) => {
      if (isChecked) {
        return channelName;
      }

      return prev === channelName ? null : prev;
    });

  const bannerData = getBannerData();

  const banner = (
    <InfoBanner>
      <BannerInfoIcon />
      <InfoBannerContent>
        <InfoBannerTitle>{t('publishWhatWillHappen')}</InfoBannerTitle>
        <InfoBannerBody>
          <InfoBannerBold>{bannerData.label} </InfoBannerBold>
          {bannerData.text}
        </InfoBannerBody>
      </InfoBannerContent>
    </InfoBanner>
  );

  return (
    <Wrapper>
      <ScrollContent>
        <Section>
          <SectionTitle>{t('publishHowNotified')}</SectionTitle>

          <ChannelList>
            <ChannelCheckboxField
              name={PUSH_CHANNEL}
              label={t('pushNotification')}
              description={t('publishChannelPushDesc')}
              fieldValue={suffixValue}
              isOpen={openChannel === PUSH_CHANNEL}
              onToggleOpen={toggleOpenChannel}
              onCheckedChange={handleCheckedChange}
              expandedContent={
                toAddMessage ? (
                  <>
                    <SubLabelRow>
                      <SubLabel>{`${t('publishMessageText')} (${t('optional')})`}</SubLabel>
                      {!!suffixValue && (
                        <ResetToDefault onClick={() => onSuffixChange('')}>{t('resetToDefault')}</ResetToDefault>
                      )}
                    </SubLabelRow>
                    <MessageInput
                      type="text"
                      value={suffixValue || ''}
                      onChange={(e) => onSuffixChange(e.target.value)}
                      placeholder={t('publishMessagePlaceholder')}
                    />
                  </>
                ) : null
              }
            />

            <ChannelLink>
              <ChannelLinkArrow />
              <ChannelLinkLabel>{channelLinkLabel}</ChannelLinkLabel>
            </ChannelLink>

            <ChannelCheckboxField
              name={EMAIL_CHANNEL}
              label={t('email')}
              description={emailDescription}
              fieldValue={emailSubjectValue}
              isOpen={openChannel === EMAIL_CHANNEL}
              onToggleOpen={toggleOpenChannel}
              onCheckedChange={handleCheckedChange}
              expandedContent={
                <Field
                  name="notification.emailSubject"
                  render={({ input }) => (
                    <>
                      <SubLabelRow>
                        <SubLabel>{`${t('publishEmailSubject')} (${t('optional')})`}</SubLabel>
                        {!!input.value && (
                          <ResetToDefault onClick={() => input.onChange('')}>{t('resetToDefault')}</ResetToDefault>
                        )}
                      </SubLabelRow>
                      <EmailSubjectInput {...input} type="text" placeholder={t('publishEmailSubjectPlaceholder')} />
                    </>
                  )}
                />
              }
            />
          </ChannelList>

          {banner}
        </Section>

        {isCheckAvailabilityNeeded && (
          <>
            <SectionDivider />

            <RadioSection>
              <SectionTitle>{t('whenToCheck')}</SectionTitle>

              <RadioOptionField
                name="notification.publish"
                value="publishNow"
                label={<RadioLabel>{t('publishNow')}</RadioLabel>}
              />

              <RadioOptionField
                name="notification.publish"
                value="publishGradually"
                disabled={isAccountNetworkAccessSupplier}
                label={
                  <GradualLabelRow>
                    <RadioLabel>{t('gradualPublishTitle')}</RadioLabel>
                    <Tooltip
                      placement="bottomLeft"
                      body={
                        <TooltipContent>
                          <TooltipText>
                            <Trans i18nKey="gradualPublishTooltipText" components={{ br: <br /> }} />
                          </TooltipText>
                        </TooltipContent>
                      }>
                      <InfoIcon />
                    </Tooltip>
                    <NewTagIcon />
                    {isAccountNetworkAccessSupplier && <LockIcon />}
                  </GradualLabelRow>
                }
              />
            </RadioSection>
          </>
        )}
      </ScrollContent>
    </Wrapper>
  );
};

export default PublishSettings;
